/**
 * Composition runtime config derived from Vite env vars. Phase 5 moved
 * `composition.service.ts` into `@benchmark/core/services` and parameterized
 * its env reads. The apps-side scribe/transcript views now centralize the
 * `import.meta.env.*` lookups here so the core service stays config-free.
 *
 * @agentic connections
 */

import type {
  CompositionSearchOptions,
  FetchTranscriptOptions,
} from "@benchmark/core/services";

/**
 * Returns the runtime mock-mode flag for the composition pipeline. Mirrors
 * the original behaviour from `composition.service.ts`: only enabled in dev,
 * never in test, and only when `VITE_MOCK_SCRIBE_COMPOSITION === "true"`.
 * @agentic connections
 */
export const compositionMockScribeEnabled = (): boolean =>
  import.meta.env.DEV &&
  import.meta.env.MODE !== "test" &&
  import.meta.env.VITE_MOCK_SCRIBE_COMPOSITION === "true";

/**
 * Returns the runtime feature flag for the new TypeScript composition
 * pipeline. Defaults to `true` unless explicitly disabled.
 * @agentic connections
 */
export const compositionEnableNewPipeline = (): boolean =>
  import.meta.env.VITE_ENABLE_NEW_PIPELINE_COMPOSITIONS !== "false";

/**
 * Returns the runtime flag for verbose pipeline-source logging.
 * @agentic connections
 */
export const compositionLogPipelineSource = (): boolean =>
  import.meta.env.VITE_LOG_COMPOSITION_PIPELINE_SOURCE === "true";

/**
 * Convenience: builds a `CompositionSearchOptions` object pre-populated with
 * the runtime env-derived flags. Callers may spread additional fields
 * (e.g. `lookbackMs`) on top.
 * @agentic connections
 */
export const compositionSearchEnvOptions = (): CompositionSearchOptions => ({
  enableNewPipeline: compositionEnableNewPipeline(),
  mockScribe: compositionMockScribeEnabled(),
  logPipelineSource: compositionLogPipelineSource(),
});

/**
 * Convenience: builds a `FetchTranscriptOptions` object pre-populated with
 * the runtime env-derived mock flag.
 * @agentic connections
 */
export const compositionTranscriptEnvOptions = (): FetchTranscriptOptions => ({
  mockScribe: compositionMockScribeEnabled(),
});
